import { ref, watch } from "vue";
import {
  saveEssayDraft,
  loadEssayDraft,
  saveLocalDraft,
  loadLocalDraft,
  markDraftSubmitted,
  clearLocalDraft
} from "@/services/essayDrafts";
import { auth } from "@/firebase";

/**
 * useAutosaveDraft
 *
 * Provides debounced autosave to Firestore (logged-in) or localStorage
 * (logged-out / offline), draft restoration on mount, and a reactive
 * save-status string for display in the UI.
 *
 * @param {import('vue').Ref<string>} essayRef  - the reactive essay text ref
 * @param {string | null | undefined} promptId  - stable id for this prompt
 * @param {{ moduleVersion?: string | null }} options
 * @returns {{ saveStatus, loadDraft, onSubmitted }}
 */
export function useAutosaveDraft(essayRef, promptId, options = {}) {
  // ── Status: '' | 'saving' | 'saved' | 'offline' ────────────────────────────
  const saveStatus = ref("");

  let debounceTimer = null;
  let savedTimer = null;

  function clearTimers() {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (savedTimer) clearTimeout(savedTimer);
  }

  async function persistDraft(text) {
    saveStatus.value = "saving";

    const isOnline = navigator.onLine;
    const isLoggedIn = !!auth.currentUser;

    // Always keep localStorage in sync as offline / fallback layer
    if (promptId) {
      saveLocalDraft(promptId, text);
    }

    if (isLoggedIn && isOnline && promptId) {
      try {
        await saveEssayDraft(promptId, text, {
          moduleVersion: options.moduleVersion || null
        });
        saveStatus.value = "saved";
      } catch {
        // Firestore failed – localStorage already saved above
        saveStatus.value = "offline";
      }
    } else if (!isOnline) {
      saveStatus.value = "offline";
    } else {
      // Logged-out: localStorage save counts as "saved"
      saveStatus.value = "saved";
    }

    // After 3 s, return to a calm neutral state
    savedTimer = setTimeout(() => {
      if (saveStatus.value === "saved") {
        saveStatus.value = "all-saved";
      }
    }, 3000);
  }

  // ── Watch + debounce ────────────────────────────────────────────────────────
  watch(essayRef, newText => {
    if (!promptId) return;
    clearTimers();
    // Show "saving…" label only after a short pause so rapid typists
    // don't see it flicker on every keystroke
    debounceTimer = setTimeout(() => {
      persistDraft(newText);
    }, 1500);
  });

  // ── Restore draft on page load ──────────────────────────────────────────────
  async function loadDraft() {
    if (!promptId) return;

    const isLoggedIn = !!auth.currentUser;

    let draft = null;

    if (isLoggedIn) {
      try {
        draft = await loadEssayDraft(promptId);
      } catch {
        // Firestore unavailable – fall through to localStorage
      }
    }

    if (!draft) {
      draft = loadLocalDraft(promptId);
    }

    // Only populate the editor if it is currently empty
    if (draft && draft.essayText && !essayRef.value) {
      essayRef.value = draft.essayText;
      saveStatus.value = "all-saved";
    }
  }

  // ── Called after successful submission ─────────────────────────────────────
  async function onSubmitted() {
    clearTimers();
    saveStatus.value = "";
    if (!promptId) return;

    clearLocalDraft(promptId);

    if (auth.currentUser) {
      try {
        await markDraftSubmitted(promptId);
      } catch {
        // non-critical
      }
    }
  }

  return { saveStatus, loadDraft, onSubmitted };
}
